import { APP_ROUTES } from './app.routes';
import { ErrorHandlerInterceptor } from '@qima-labs/app/shared/interceptors/error-handler.interceptors';
import { LanguageInterceptor } from '@qima-labs/app/shared/interceptors/language.interceptor';
import { dateFactory } from '@qsp-ui-library/factories/date-factory/date.factory';
import { httpLoaderFactory, localInitProvider } from '@qsp-ui-library/factories/i18n.factory';
import { translationFactory } from '@qsp-ui-library/factories/translation.factory';
import { AuthInterceptor } from '@qsp-ui-library/interceptors/auth.interceptor';
import { APPCUES_ACCOUNT_ID_PROVIDER } from '@qsp-ui-library/providers/appcues-account-id.provider';
import { BROWSER_WINDOW_PROVIDER } from '@qsp-ui-library/providers/browser-window.provider';
import { GOOGLE_ANALYTICS_AUTH_PROVIDER } from '@qsp-ui-library/providers/google-analytics-auth.provider';
import { GOOGLE_ANALYTICS_ID_PROVIDER } from '@qsp-ui-library/providers/google-analytics-id.provider';
import { GOOGLE_ANALYTICS_INIT_PROVIDER } from '@qsp-ui-library/providers/google-analytics-init.provider';
import { GOOGLE_ANALYTICS_PREVIEW_PROVIDER } from '@qsp-ui-library/providers/google-analytics-preview.provider';
import { LOGROCKET_ENVIRONMENT_ID_INIT_PROVIDER } from '@qsp-ui-library/providers/logrocket-environment-id-init.provider';
import { LOGROCKET_ENVIRONMENT_ID_PROVIDER } from '@qsp-ui-library/providers/logrocket-environment-id.provider';
import { SERVICE_WORKER_PROVIDER } from '@qsp-ui-library/providers/service-worker.provider';
import { SHOULD_SESSION_STORAGE_ENCRYPT_PROVIDER } from '@qsp-ui-library/providers/should-session-storage-encrypt.provider';
import { WINDOW_PROVIDER } from '@qsp-ui-library/providers/window.provider';
import { AppBrowserDetectService } from '@qsp-ui-library/services/browser-services/app-browser-detect/app-browser-detect.service';
import { HttpAuthInterceptor } from '@qsp-ui-library/services/http-services/http-auth-handler/http-auth-interceptor.service';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FullscreenOverlayContainer, OverlayContainer, OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, FactoryProvider, importProvidersFrom, LOCALE_ID, ValueProvider } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EQimaLoggerLevel,
  QIMA_ASSETS_PATH_TOKEN,
  QIMA_DATE_FACTORY_TOKEN,
  QIMA_TRANSLATION_FACTORY_TOKEN,
  QimaDateModule,
  QimaIconModule,
  QimaLoggerModule,
  QimaModule,
  QimaSnackbarService,
  QimaTranslationModule,
} from '@qima/ngx-qima';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CookieModule } from 'ngx-cookie';

export const APP_CONFIG: ApplicationConfig = {
  providers: [
    {
      provide: OverlayContainer,
      useClass: FullscreenOverlayContainer,
    },
    ClipboardModule,
    provideHttpClient(),
    QimaSnackbarService,
    HttpAuthInterceptor,
    WINDOW_PROVIDER,
    BROWSER_WINDOW_PROVIDER,
    SHOULD_SESSION_STORAGE_ENCRYPT_PROVIDER,
    SERVICE_WORKER_PROVIDER,
    GOOGLE_ANALYTICS_ID_PROVIDER,
    GOOGLE_ANALYTICS_AUTH_PROVIDER,
    GOOGLE_ANALYTICS_PREVIEW_PROVIDER,
    GOOGLE_ANALYTICS_INIT_PROVIDER,
    APPCUES_ACCOUNT_ID_PROVIDER,
    LOGROCKET_ENVIRONMENT_ID_PROVIDER,
    LOGROCKET_ENVIRONMENT_ID_INIT_PROVIDER,
    ReactiveFormsModule,
    {
      provide: APP_INITIALIZER,
      useFactory: localInitProvider,
      deps: [TranslateService, AppBrowserDetectService],
      multi: true,
    },
    {
      provide: QIMA_DATE_FACTORY_TOKEN,
      useFactory: dateFactory,
    } as FactoryProvider,
    {
      provide: QIMA_ASSETS_PATH_TOKEN,
      useValue: 'ngx-qima/assets/',
    } as ValueProvider,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: QIMA_TRANSLATION_FACTORY_TOKEN,
      useFactory: translationFactory,
      deps: [TranslateService, LOCALE_ID],
    } as FactoryProvider,
    provideRouter(APP_ROUTES),
    importProvidersFrom(BrowserModule),
    importProvidersFrom(BrowserAnimationsModule),
    importProvidersFrom(QimaModule.forRoot()),
    importProvidersFrom(QimaDateModule),
    importProvidersFrom(QimaIconModule),
    importProvidersFrom(OverlayModule),
    importProvidersFrom(QimaTranslationModule.forRoot()),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpBackend],
        },
      })
    ),
    // Used by ngx-qima library
    importProvidersFrom(AngularSvgIconModule.forRoot()),
    importProvidersFrom(CookieModule.forRoot()),
    importProvidersFrom(
      QimaLoggerModule.forRoot({
        loggerLevel: EQimaLoggerLevel.ERROR,
      })
    ),
    importProvidersFrom(FormsModule),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: true,
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      })
    ),
  ],
};
